import { Color, Quality } from 'common';
import Data from 'data';
import Locale from 'data/locale';
import { itemCheckMod, itemGetTypes, modsToStats, formatStr, itemGetType, uniqueStats, runewordStats, gemStats, itemsetPartStats } from './item';

function itemListMods(id, quality, magic) {
  const item = Data.items[id];
  const types = itemGetTypes(id);

  function listMods(modList, name, gid, supt) {
    return Object.keys(modList).filter(id => {
      const mod = Data.mods[id];
      if (supt != null && !mod[supt]) return false;
      if (gid != null && mod.group !== gid) return false;
      if (name != null && (Locale.strings[mod.name] || mod.name) !== name) return false;
      return itemCheckMod(mod, item, types, quality);
    });
  }

  const result = {};
  if (quality === Quality.MAGIC || quality === Quality.RARE || quality === Quality.CRAFTED) {
    result.prefix = listMods(Data.magicPrefix, magic ? (magic.prefix || '') : null);
    result.suffix = listMods(Data.magicSuffix, magic ? (magic.suffix || '') : null);
  }
  if (item.autoprefix) {
    result.auto = listMods(Data.autoMagic, null, item.autoprefix);
  }
  if (quality === Quality.CRAFTED) {
    result.crafted = listMods(Data.crafted);
  }
  if (quality === Quality.SUPERIOR || quality === Quality.RUNEWORD) {
    const supt = itemGetType(types);
    result.superior = listMods(Data.qualityItems, null, null, supt);
  }
  if (Data.itemTypes[item.type]?.staffmods && (quality !== Quality.UNIQUE && quality !== Quality.SET)) {
    result.staff = listMods(Data.staffMods);
  }
  return result;
}

function findUniques(str) {
  const list = [];
  for (const id in Data.uniqueItems) {
    const item = Data.uniqueItems[id];
    const name = (Locale.strings[item.index] || item.index);
    if (str === name) list.push(id);
  }
  for (const id in Data.setItems) {
    const item = Data.setItems[id];
    const name = (Locale.strings[item.index] || item.index);
    if (str === name) list.push(id);
  }
  for (const id in Data.runes) {
    const item = Data.runes[id];
    const name = (Locale.strings[item.name] || item.name);
    if (str === name) list.push(id);
  }
  return list;
}

function findUniqueUnid(base, color) {
  if (color === Color.GOLD) {
    const opts = Object.keys(Data.uniqueItems).filter(id => Data.uniqueItems[id].code === base);
    if (opts.length === 1) return opts[0];
  }
  if (color === Color.GREEN) {
    const opts = Object.keys(Data.setItems).filter(id => Data.setItems[id].item === base);
    if (opts.length === 1) return opts[0];
  }
}

function findBase(str) {
  for (const id in Data.items) {
    const item = Data.items[id];
    const name = (Locale.strings[item.namestr] || item.name);
    if (str === name) return id;

    if (name.includes('\n')) {
      const lines = name.split('\n');
      if (str === lines[lines.length - 1]) return id;
    }
  }
}

function parseMagicName(str) {
  const prefixes = {}, suffixes = {};
  for (const mod of Object.values(Data.magicPrefix)) {
    prefixes[Locale.strings[mod.name] || mod.name] = true;
  }
  for (const mod of Object.values(Data.magicSuffix)) {
    suffixes[Locale.strings[mod.name] || mod.name] = true;
  }
  function parseSuffix(str) {
    if (!str) {
      return {};
    }
    for (const name in suffixes) {
      if (str === " " + name) {
        return {suffix: name};
      }
    }
  }
  function parseBase(str) {
    for (const id in Data.items) {
      const item = Data.items[id];
      const name = (Locale.strings[item.namestr] || item.name);
      if (str.substring(0, name.length) === name) {
        const suf = parseSuffix(str.substring(name.length));
        if (suf) return {...suf, base: id};
      }
    }
  }

  for (const name in prefixes) {
    const pre = name + " ";
    if (str.substring(0, pre.length) === pre) {
      const next = parseBase(str.substring(pre.length));
      if (next) return {...next, prefix: name};
    }
  }
  return parseBase(str);
}

function parseWhiteName(str) {
  const names = ['Hiquality', 'Crude', 'Cracked', 'Damaged', 'Low Quality'];
  for (const id of names) {
    const name = (Locale.strings[id] || id) + " ";
    if (str.substring(0, name.length) === name) {
      const base = findBase(str.substring(name.length));
      if (base) return {base, quality: (id === 'Hiquality' ? Quality.SUPERIOR : Quality.LOW)};
    }
  }
  const base = findBase(str);
  if (base) return {base, quality: Quality.NORMAL};
}

function parseStats(statLines, stats) {
  function findStat(re) {
    re = new RegExp("^" + re + "$");
    for (let i = 0; i < statLines.length; ++i) {
      const m = statLines[i].match(re);
      if (m) {
        statLines.splice(i, 1);
        return m;
      }
    }
  }

  const statVals = {};
  let m;

  function escape(str) {
    return str.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
  const numStr = "(-?\\d+)";

  if (stats.strength && stats.energy && stats.dexterity && stats.vitality) {
    m = findStat(`\\+?${numStr} ${escape(Locale.strings.Moditem2allattrib)}`);
    if (m) {
      const value = parseInt(m[1]);
      statVals.strength = value;
      statVals.energy = value;
      statVals.dexterity = value;
      statVals.vitality = value;
    }
  }

  if (stats.fireresist && stats.lightresist && stats.coldresist && stats.poisonresist) {
    m = findStat(`${formatStr(escape(Locale.strings.strModAllResistances), numStr)}`);
    if (m) {
      const value = parseInt(m[1]);
      statVals.fireresist = value;
      statVals.lightresist = value;
      statVals.coldresist = value;
      statVals.poisonresist = value;
    }
  }

  if (stats.mindamage && stats.maxdamage) {
    m = findStat(`${formatStr(escape(Locale.strings.strModMinDamage), numStr)}`);
    if (m) {
      const value = parseInt(m[1]);
      statVals.mindamage = value;
      statVals.maxdamage = value;
    }
    m = findStat(`${formatStr(escape(Locale.strings.strModMinDamageRange), numStr, numStr)}`);
    if (m) {
      statVals.mindamage = parseInt(m[1]);
      statVals.maxdamage = parseInt(m[2]);
    }
  }

  if (stats.firemindam && stats.firemaxdam) {
    m = findStat(`${formatStr(escape(Locale.strings.strModFireDamage), numStr)}`);
    if (m) {
      const value = parseInt(m[1]);
      statVals.firemindam = value;
      statVals.firemaxdam = value;
    }
    m = findStat(`${formatStr(escape(Locale.strings.strModFireDamageRange), numStr, numStr)}`);
    if (m) {
      statVals.firemindam = parseInt(m[1]);
      statVals.firemaxdam = parseInt(m[2]);
    }
  }

  if (stats.coldmindam && stats.coldmaxdam) {
    m = findStat(`${formatStr(escape(Locale.strings.strModColdDamage), numStr)}`);
    if (m) {
      const value = parseInt(m[1]);
      statVals.coldmindam = value;
      statVals.coldmaxdam = value;
    }
    m = findStat(`${formatStr(escape(Locale.strings.strModColdDamageRange), numStr, numStr)}`);
    if (m) {
      statVals.coldmindam = parseInt(m[1]);
      statVals.coldmaxdam = parseInt(m[2]);
    }
  }

  if (stats.lightmindam && stats.lightmaxdam) {
    m = findStat(`${formatStr(escape(Locale.strings.strModLightningDamage), numStr)}`);
    if (m) {
      const value = parseInt(m[1]);
      statVals.lightmindam = value;
      statVals.lightmaxdam = value;
    }
    m = findStat(`${formatStr(escape(Locale.strings.strModLightningDamageRange), numStr, numStr)}`);
    if (m) {
      statVals.lightmindam = parseInt(m[1]);
      statVals.lightmaxdam = parseInt(m[2]);
    }
  }

  if (stats.magicmindam && stats.magicmaxdam) {
    m = findStat(`${formatStr(escape(Locale.strings.strModMagicDamage), numStr)}`);
    if (m) {
      const value = parseInt(m[1]);
      statVals.magicmindam = value;
      statVals.magicmaxdam = value;
    }
    m = findStat(`${formatStr(escape(Locale.strings.strModMagicDamageRange), numStr, numStr)}`);
    if (m) {
      statVals.magicmindam = parseInt(m[1]);
      statVals.magicmaxdam = parseInt(m[2]);
    }
  }

  if (stats.poisonmindam && stats.poisonmaxdam && stats.poisonlength) {
    const scale = (value, length) => ({min: Math.ceil((256 * value - 128) / length), max: Math.ceil((256 * value + 128) / length) - 1});
    m = findStat(`${formatStr(escape(Locale.strings.strModPoisonDamage), numStr, numStr)}`);
    if (m) {
      const value = parseInt(m[1]);
      const length = parseInt(m[2]) * 25;
      statVals.poisonmindam = scale(value, length);
      statVals.poisonmaxdam = scale(value, length);
      statVals.poisonlength = length;
    }
    m = findStat(`${formatStr(escape(Locale.strings.strModPoisonDamageRange), numStr, numStr, numStr)}`);
    if (m) {
      const value1 = parseInt(m[1]);
      const value2 = parseInt(m[2]);
      const length = parseInt(m[3]) * 25;
      statVals.poisonmindam = scale(value1, length);
      statVals.poisonmaxdam = scale(value2, length);
      statVals.poisonlength = length;
    }
  }

  if (stats.item_mindamage_percent && stats.item_maxdamage_percent) {
    m = findStat(`\\+?${numStr}% ${escape(Locale.istrings[10023])}`);
    if (m) {
      const value = parseInt(m[1]);
      statVals.item_mindamage_percent = value;
      statVals.item_maxdamage_percent = value;
    }
  }

  for (const idFull in stats) {
    let [id, param, param2, param3] = idFull.split('#');
    const stat = Data.itemStatCost[id];
    if (!stat || !stat.descfunc) continue;
    if (param2 === 'x') {

      if (stat.descfunc === 13) {
        for (let cls = 0; cls < Data.strClassSkillsStat.length; ++cls) {
          const text = `+${param} ${Locale.istrings[Data.strClassSkillsStat[cls]]}`;
          const index = statLines.indexOf(text);
          if (index >= 0) {
            statVals[idFull] = cls;
            statLines.splice(index, 1);
            break;
          }
        }
      } else if (stat.descfunc === 27) {
        for (let cls = 0; cls < Data.strClassOnly.length; ++cls) {
          const m = findStat(`\\+${param} ${Locale.strings.ItemStast1k} (.*) ${escape(Locale.istrings[Data.strClassOnly[cls]])}`);
          if (m) {
            const skill = Object.keys(Data.skills).find(id => {
              const skill = Data.skills[id];
              if (skill.charclass !== Data.classes[cls]) return false;
              const skilldesc = Data.skillDesc[skill.skilldesc];
              return Locale.strings[skilldesc.strname] === m[1];
            });
            if (skill) {
              statVals[idFull] = parseInt(skill);
            }
            break;
          }
        }
      }
      continue;
    }

    function tryParse(descstr) {
      let str = descstr && escape(Locale.strings[descstr]);
      let str2 = stat.descfunc >= 6 && stat.descfunc <= 10 && stat.descstr2 && escape(Locale.strings[stat.descstr2]);
      const skillName = id => {
        const skill = Data.skills[id];
        const skilldesc = skill && Data.skillDesc[skill.skilldesc];
        return escape(Locale.strings[skilldesc.strname]);
      };
      const skillClass = id => {
        const skill = Data.skills[id];
        if (!skill.charclass) return '';
        return escape(Locale.istrings[Data.strClassOnly[Data.classes.indexOf(skill.charclass)]]);
      };
      let value = numStr;
      let transform = null;
      switch (stat.descfunc) {
      case 1: case 6:
        value = "\\+?" + value;
        break;
      case 2: case 7:
        value += "%";
        break;
      case 3: case 9:
        break;
      case 4: case 8:
        value = "\\+?" + value;
        value += "%";
        break;
      case 5: case 10:
        transform = "per128";
        value += "%";
        break;
      case 11:
        transform = "div100";
        str = formatStr(escape(Locale.strings.ModStre9u), 1, value);
        break;
      case 12:
        str = `${str}(?: \\+?${value})?`;
        break;
      case 13:
        value = "\\+?" + value;
        str = escape(Locale.istrings[Data.strClassSkillsStat[param]]);
        break;
      case 14:
        str = formatStr(escape(Locale.istrings[Data.strSkilltabStat[param]]), value);
        str2 = escape(Locale.istrings[Data.strClassOnly[Math.floor(param / 3)]]);
        break;
      case 15:
        str = formatStr(str, param2, param3 === 'x' ? '\\d+' : value, skillName(param));
        break;
      case 16:
        str = formatStr(str, value, skillName(param));
        break;
      case 20:
        transform = "neg";
        value += "%";
        break;
      case 21:
        transform = "neg";
        break;
      case 22:
      case 23:
        value += "%";
        // replace param with monster type
        str += " " + param;
        break;
      case 24:
        str = `${Locale.strings.skilldesc1}${numStr} ${skillName(param)} ${formatStr(str, numStr, numStr)}`;
        break;
      case 27:
        str = `\\+${value} ${Locale.strings.ItemStast1k} ${skillName(param)} ${skillClass(param)}`
        break;
      case 28:
        str = `\\+${value} ${Locale.strings.ItemStast1k} ${skillName(param)}`
        break;
        // no default
      }
      if (stat.descval === 1) {
        str = `${value} ${str}`;
      } else if (stat.descval === 2 && stat.descfunc !== 12) {
        str = `${str} ${value}`;
      }
      if (str2) {
        str = `${str} ${str2}`;
      }

      const m = findStat(str);
      if (m) {
        let destId = idFull;
        if (stat.descfunc === 24) {
          destId = `${id}#${param}#${m[1]}`;
          m[1] = m[2];
        }
        if (m[1]) {
          value = parseInt(m[1]);
          if (transform === "neg") {
            value = -value;
          } else if (transform === "per128") {
            value = Math.round(value * 128 / 100);
          } else if (transform === "div100") {
            value = Math.round(100 / value);
          }
          if (stat.descfunc === 28 && value === 3) {
            statVals[destId] = {min: 3, max: 1000};
          } else if (stat.descfunc >= 6 && stat.descfunc <= 10) {
            const factor = (1 << stat.opparam);
            statVals[destId] = {min: Math.ceil(factor * value / 99), max: factor * (value + 1) - 1};
          } else {
            statVals[destId] = value;
          }
        } else if (stat.descfunc === 12) {
          statVals[destId] = 1;
        } else {
          statVals[destId] = {min: 1, max: 1000};
        }
      }
    }
    tryParse(stat.descstrpos);
    if (stat.descstrneg && stat.descstrneg !== stat.descstrpos) {
      tryParse(stat.descstrneg, true);
    }
  }  

  return statVals;
}

function parseMods(stats, modVals, quality, presetMods) {

  // build a target vector
  const target = {};
  switch (quality) {
  case Quality.SUPERIOR:
    target.n_superior = {min: 1, max: 1};
    break;
  case Quality.MAGIC:
    target.n_prefix = {min: 0, max: 1};
    target.n_suffix = {min: 0, max: 1};
    target.n_affix = {min: 1, max: 2};
    break;
  case Quality.RARE:
    target.n_prefix = {min: 0, max: 3};
    target.n_suffix = {min: 0, max: 3};
    target.n_affix = {min: 3, max: 6};
    break;
  case Quality.CRAFTED:
    target.n_crafted = {min: 1, max: 1};
    target.n_prefix = {min: 0, max: 3};
    target.n_suffix = {min: 0, max: 3};
    target.n_affix = {min: 1, max: 4};
    break;
  case Quality.RUNEWORD:
    target.n_superior = {min: 0, max: 1};
    break;
  // no default
  }
  if (presetMods) {
    for (const id of presetMods) {
      target[`m_${id}`] = {min: 1, max: 1};
    }
  }
  if (Object.keys(modVals).some(id => Data.autoMagic[id])) {
    target.n_auto = {min: 1, max: 1};
  }
  if (modVals.runeword) {
    target.n_runeword = {min: 1, max: 1};
  }
  for (const id in stats) {
    if (typeof stats[id] === 'object') {
      target[id] = stats[id];
    } else {
      target[id] = {min: stats[id], max: stats[id]};
    }
  }

  // build source vectors
  const groups = {};
  const modLimits = {};
  const negative = new Set();
  for (const [id, mstat] of Object.entries(modVals)) {
    const modsrc = {};
    if (Data.magicPrefix[id]) {
      modsrc.n_prefix = {min: 1, max: 1};
      modsrc.n_affix = {min: 1, max: 1};
    }
    if (Data.magicSuffix[id]) {
      modsrc.n_suffix = {min: 1, max: 1};
      modsrc.n_affix = {min: 1, max: 1};
    }
    if (Data.crafted[id]) {
      modsrc.n_crafted = {min: 1, max: 1};
    }
    if (Data.autoMagic[id]) {
      modsrc.n_auto = {min: 1, max: 1};
    }
    if (Data.qualityItems[id]) {
      modsrc.n_superior = {min: 1, max: 1};
    }
    if (id === 'runeword') {
      modsrc.n_runeword = {min: 1, max: 1};
    }
    if (presetMods && presetMods.includes(id)) {
      modsrc[`m_${id}`] = {min: 1, max: 1};
    }

    const usedIndices = {};
    let repeats = 0;
    for (const [sid, value] of Object.entries(mstat)) {
      if (sid === 'coldlength') {
        continue; // this does not appear on tooltip in any way so we have to skip it
      }
      let m = sid.match(/item_charged_skill#(\d+)#/);
      if (m) {
        const nstat = Object.keys(stats).find(stat => stat.includes(m[0])) || sid;
        const nval = stats[nstat] || value;
        modsrc[nstat] = {min: nval, max: nval};
      } else if (typeof value === "number") {
        modsrc[sid] = {min: value, max: value};
        if (value < 0) negative.add(sid);
      } else if ((m = value.match(/^{(\d)}$/))) {
        const mod = Data.mods[id];
        const min = mod[`mod${m[1]}min`];
        const max = mod[`mod${m[1]}max`];
        const index = parseInt(m[1]) - 1;
        modsrc[sid] = {min, max, index};
        if (usedIndices[index]) repeats += 1;
        usedIndices[index] = true;
        if (min < 0) negative.add(sid);
      }
    }

    if (Object.keys(modsrc).every(sid => target[sid])) {
      const gid = Data.mods[id].group || 0;
      groups[gid] = (groups[gid] || {mods: {}, repeats: 0});
      groups[gid].mods[id] = modsrc;
      groups[gid].repeats = Math.max(groups[gid].repeats, repeats);

      modLimits[id] = modsrc;
    }
  }

  // we only work with positive values, so flip the negative stats
  // we assume that no stats can be both positive and negative
  const negate = ({min, max}) => ({min: -max, max: -min});
  for (const id of negative) {
    if (!target[id]) continue;
    target[id] = negate(target[id]);
    for (const mod of Object.values(modLimits)) {
      if (mod[id]) mod[id] = negate(mod[id]);
    }
  }

  // build a list of source groups and calculate their per-stat maximums  
  const source = [];
  for (const group of Object.values(groups)) {
    group.max = {};
    for (const mod of Object.values(group.mods)) {
      for (const id in target) {
        mod[id] = (mod[id] || {min: 0, max: 0});
        group.max[id] = Math.max(group.max[id] || 0, mod[id].max);
      }
    }
    source.push(group);
  }

  // put mods with repeated param (like res-all) at the end
  source.sort((a, b) => a.repeats - b.repeats);

  // build a list of partial sums for pruning
  const reverse = [];
  reverse[source.length] = {};
  for (const id in target) {
    reverse[source.length][id] = 0;
  }
  for (let i = source.length - 1; i >= 0; --i) {
    reverse[i] = {...reverse[i + 1]};
    for (const id in source[i].max) {
      reverse[i][id] += source[i].max[id];
    }
  }

  // recursive brute-force function
  const result = [];
  const ids = Object.keys(target);
  function Iterate(i, tmin, tmax) {
    if (i >= source.length) {
      // end of input reached, check the result
      // this shouldn't be necessary since we did all the checks before, but just in case
      return ids.every(id => tmin[id] <= 0 && tmax[id] >= 0);
    }

    const next = reverse[i + 1];
    for (const m in source[i].mods) {
      const src = source[i].mods[m];

      const pmin = [], pmax = [];
      for (const id in stats) {
        const {min, max, index} = src[id];
        if (index != null) {
          const cmin = Math.max(min, tmin[id] - next[id]);
          const cmax = Math.min(max, tmax[id]);
          pmin[index] = (pmin[index] == null ? cmin : Math.max(pmin[index], cmin));
          pmax[index] = (pmax[index] == null ? cmax : Math.min(pmax[index], cmax));
        }
      }

      // try to add the current stat
      if (ids.every(id => src[id].min <= tmax[id] && src[id].max + next[id] >= tmin[id]) && Object.keys(pmin).every(i => pmin[i] <= pmax[i])) {
        const nmin = {...tmin};
        const nmax = {...tmax};
        for (const id in src) {
          const {min, max, index} = src[id];
          if (index != null) {
            nmin[id] -= pmax[index];
            nmax[id] -= pmin[index];
          } else {
            nmin[id] -= max;
            nmax[id] -= min;
          }
        }
        result.push(m);
        if (Iterate(i + 1, nmin, nmax)) {
          return true;
        }
        result.pop();
      }
    }

    // try to skip current group
    if (ids.every(id => next[id] >= tmin[id])) {
      return Iterate(i + 1, tmin, tmax);
    }
  }

  // unwrap objects a little bit
  const tmin = {}, tmax = {};
  for (const id in target) {
    tmin[id] = target[id].min;
    tmax[id] = target[id].max;
  }
  
  if (Iterate(0, tmin, tmax)) {
    // put mods with repeated param at the *start* now
    result.reverse();

    // calculate min/max per stat again
    const rmin = {}, rmax = {}, nmin = {}, nmax = {};
    for (const sid in stats) {
      rmin[sid] = rmax[sid] = 0;
      nmin[sid] = (typeof stats[sid] === "object" ? stats[sid].min : stats[sid]);
      nmax[sid] = (typeof stats[sid] === "object" ? stats[sid].max : stats[sid]);
      for (const id of result) {
        rmin[sid] += modLimits[id][sid].min;
        rmax[sid] += modLimits[id][sid].max;
      }
    }
    // interpolate stats
    const output = {};
    for (const id of result) {
      const values = [];
      const modi = Data.mods[id];
      if (modi.mod1max != null) values[0] = modi.mod1max;
      if (modi.mod2max != null) values[1] = modi.mod2max;
      if (modi.mod3max != null) values[2] = modi.mod3max;
      if (modi.mod4max != null) values[3] = modi.mod4max;
      if (modi.mod5max != null) values[4] = modi.mod5max;
      if (modi.mod6max != null) values[5] = modi.mod6max;
      if (modi.mod7max != null) values[6] = modi.mod7max;

      const pmin = [], pmax = [];
      for (const sid in stats) {
        const {min, max, index} = modLimits[id][sid];
        if (index != null) {
          const cmin = Math.max(min, nmin[sid] + max - rmax[sid]);
          const cmax = Math.min(max, nmax[sid] + min - rmin[sid]);
          pmin[index] = (pmin[index] == null ? cmin : Math.max(pmin[index], cmin));
          pmax[index] = (pmax[index] == null ? cmax : Math.min(pmax[index], cmax));
        }
      }

      for (const sid in stats) {
        const {min, max, index} = modLimits[id][sid];
        if (index != null) {
          if (pmin[index] > pmax[index]) {
            // this shouldn't happen?
            return null;
          }
          const value = (pmin[index] + pmax[index]) >> 1;
          nmin[sid] -= value;
          nmax[sid] -= value;
          values[index] = negative.has(sid) ? -value : value;
        } else {
          // here min=max
          nmin[sid] -= min;
          nmax[sid] -= max;
        }
        rmin[sid] -= min;
        rmax[sid] -= max;
      }

      output[id] = values;
    }
    return output;
  }
}

function subtractStats(dst, src, orig) {
  for (const id in src) {
    if (orig && orig[id] && orig[id] < 0) {
      // special case
      dst[id] = (dst[id] || 0) - src[id];
      continue;
    }
    if (id === 'coldlength') continue;
    if (!dst[id]) return false;
    if (typeof dst[id] === 'object') {
      if (Math.max(Math.abs(dst[id].min), Math.abs(dst[id].max)) < Math.abs(src[id])) return false;
      dst[id].min -= src[id];
      dst[id].max -= src[id];
    } else {
      if (Math.abs(dst[id]) < Math.abs(src[id])) return false;
      dst[id] -= src[id];
      if (!dst[id]) delete dst[id];
    }
  }
  return true;
}

export function parseUniqueStats(item, getStats) {
  const uniq = Data.uniqueItems[item.unique] || Data.setItems[item.unique];
  if (!uniq) return null;
  const values = [];
  for (let i = 0; i < 12; ++i) {
    if (!uniq[`prop${i + 1}`]) continue;
    const min = uniq[`min${i + 1}`], max = uniq[`max${i + 1}`];
    values.push((min === max || max === 0) ? max : `{${i}}`);
  }
  const statsSrc = uniqueStats({}, uniq, values, item);

  const uniqueValues = [];
  const stats = getStats(statsSrc);
  if (!stats) return null;

  for (const id in statsSrc) {
    const descFunc = Data.itemStatCost[id]?.descfunc;
    if (descFunc || stats[id] != null) {
      if (stats[id] == null) return null;
      if ((descFunc < 6 || descFunc > 10) && typeof statsSrc[id] === 'number') {
        if (typeof stats[id] === 'object' ? (statsSrc[id] < stats[id].min || statsSrc[id] > stats[id].max) : (statsSrc[id] !== stats[id])) {
          return null;
        }
      }
    }
    if (typeof statsSrc[id] === 'string') {
      const m = statsSrc[id].match(/{(\d+)}/);
      if (m) {
        const index = parseInt(m[1]);
        const min = (uniqueValues[index] != null ? uniqueValues[index] : uniq[`min${index + 1}`]);
        const max = (uniqueValues[index] != null ? uniqueValues[index] : uniq[`max${index + 1}`]);
        if (typeof stats[id] === 'object') {
          if (min > stats[id].max || max < stats[id].min) return null;
          uniqueValues[index] = (Math.max(stats[id].min, min) + Math.min(stats[id].max, max)) >> 1;
        } else {
          if (stats[id] < min || stats[id] > max) return null;
          uniqueValues[index] = stats[id];
        }
      }
    }
  }

  for (let i = 0; i < 12; ++i) {
    if (!uniq[`prop${i + 1}`]) continue;
    if (uniqueValues[i] != null) continue;
    uniqueValues[i] = uniq[`max${i + 1}`];
  }
  return uniqueValues;
}

export function parseRunewordStats(item, socketStats, getStats, presetMods) {
  const runeword = Data.runes[item.unique];
  if (!runeword) return null;
  const mods = itemListMods(item.base, item.quality);

  const allStats = {...socketStats};
  const modStats = {};
  for (const type in mods) {
    for (const id of mods[type]) {
      modStats[id] = modsToStats({}, {[id]: ['{1}', '{2}', '{3}', '{4}']});
      delete modStats[id].poison_count;
      Object.assign(allStats, modStats[id]);
    }
  }

  const rwmod = {group: 'runeword'};
  const rwvals = [];
  for (let i = 1; i <= 7; ++i) {
    if (runeword[`t1code${i}`]) {
      rwmod[`mod${i}code`] = runeword[`t1code${i}`];
      rwmod[`mod${i}param`] = runeword[`t1param${i}`];
      const min = rwmod[`mod${i}min`] = runeword[`t1min${i}`];
      const max = rwmod[`mod${i}max`] = runeword[`t1max${i}`];
      rwvals.push(min === max || max === 0 ? max : `{${i}}`);
    }
  }
  modStats.runeword = runewordStats({}, runeword, rwvals, item);
  Object.assign(allStats, modStats.runeword);

  const stats = getStats(allStats);
  if (!stats) return null;
  if (socketStats && !subtractStats(stats, socketStats)) return null;

  Data.mods.runeword = rwmod;
  const parsed = parseMods(stats, modStats, item.quality, presetMods);
  delete Data.mods.runeword;

  if (!parsed) return null;
  const result = {};
  for (const id in parsed) {
    if (Data.autoMagic[id]) {
      if (!result.auto) result.auto = {};
      result.auto[id] = parsed[id];
    } else if (Data.qualityItems[id]) {
      if (!result.superior) result.superior = {};
      result.superior[id] = parsed[id];
    } else if (Data.staffMods[id]) {
      if (!result.staff) result.staff = {};
      result.staff[id] = parsed[id];
    } else if (id === 'runeword') {
      result.uniqueValues = parsed[id];
    }
  }
  if (!result.uniqueValues) {
    return null;
  }
  return result;
}

export function parseModStats(item, magic, socketStats, levelreq, getStats, presetMods) {
  const mods = itemListMods(item.base, item.quality, magic);

  const statsSrc = {...socketStats};
  const modStats = {};
  for (const type in mods) {
    for (const id of mods[type]) {
      const mod = Data.mods[id];
      if (levelreq != null && !Data.autoMagic[id] && (mod.classlevelreq || mod.levelreq) > levelreq) continue;

      modStats[id] = modsToStats({}, {[id]: ['{1}', '{2}', '{3}', '{4}']});
      delete modStats[id].poison_count;
      Object.assign(statsSrc, modStats[id]);
    }
  }

  const stats = getStats(statsSrc);
  if (!stats) return null;
  if (socketStats && !subtractStats(stats, socketStats)) return null;

  const parsed = parseMods(stats, modStats, item.quality, presetMods);
  if (!parsed) return null;
  const result = {};
  for (const id in parsed) {
    if (Data.magicPrefix[id] || Data.magicSuffix[id]) {
      if (!result.mods) result.mods = {};
      result.mods[id] = parsed[id];
    } else if (Data.autoMagic[id]) {
      if (!result.auto) result.auto = {};
      result.auto[id] = parsed[id];
    } else if (Data.crafted[id]) {
      if (!result.crafted) result.crafted = {};
      result.crafted[id] = parsed[id];
    } else if (Data.qualityItems[id]) {
      if (!result.superior) result.superior = {};
      result.superior[id] = parsed[id];
    } else if (Data.staffMods[id]) {
      if (!result.staff) result.staff = {};
      result.staff[id] = parsed[id];
    }
  }
  return result;
}

export default function parseItem({description, sockets}) {
  let m = description.match(/^(.*)\$\d+$/);
  if (m) description = m[1];
  let lines = description.split("\\n");

  const result = {};

  m = lines[0].match(/^(.*)\((\d+)\)/);
  if (m) {
    result.ilvl = parseInt(m[2]);
    lines[0] = m[1].trim();
  } else {
    result.ilvl = 99;
  }

  let color = 0;
  while ((m = lines[0].match(/^\\xffc([\d:;<])/))) {
    color = m[1].charCodeAt(0) - 48;
    lines[0] = lines[0].substring(6);
  }
  const olines = lines;
  lines = lines.map(line => line.replace(/\\xffc[\d:;<]/g, "").trim());

  if (lines.includes(Locale.istrings[3455])) {
    result.unidentified = true;
  }

  let uniques = null;
  if (!result.unidentified && (color === Color.RED || color === Color.GREEN || color === Color.GOLD)) {
    uniques = findUniques(lines[0]);
    result.unique = uniques[0];
  }
  if (lines.length > 1 && color !== Color.WHITE && color !== Color.BLUE && color !== Color.GRAY) {
    result.base = findBase(lines[1]);
  }
  if (!result.base && result.unique) {
    if (Data.uniqueItems[result.unique]) {
      result.base = Data.uniqueItems[result.unique].code;
    }
    if (Data.setItems[result.unique]) {
      result.base = Data.setItems[result.unique].item;
    }
  }
  let magic = null;
  if (!result.base) {
    if (color === Color.BLUE) {
      magic = parseMagicName(lines[0]);
      if (magic) {
        result.base = magic.base;
      }
    } else if (color === Color.WHITE || color === Color.GRAY) {
      const white = parseWhiteName(lines[0]);
      if (white) Object.assign(result, white);
    } else {
      result.base = findBase(lines[0]);
    }
  }

  if (!result.base) return null;

  if (!result.unique && result.unidentified) {
    result.unique = findUniqueUnid(result.base, color);
  }

  const types = itemGetTypes(result.base);
  const cat = ['weap', 'armo', 'ring', 'amul', 'char', 'jewl'].find(t => types.has(t));
  if (!cat) {
    result.quality = Quality.NORMAL;
    return result;
  }

  if (result.quality == null) {
    if (result.unique) {
      if (Data.uniqueItems[result.unique]) result.quality = Quality.UNIQUE;
      if (Data.setItems[result.unique]) result.quality = Quality.SET;
      if (Data.runes[result.unique]) result.quality = Quality.RUNEWORD;
    } else {
      result.quality = Data.qualityColor.indexOf(color);
    }
  }

  if (result.quality == null || result.quality < 0) return null;

  if (result.quality === Quality.RARE || result.quality === Quality.CRAFTED) {
    result.name = lines[0];
  }

  const statLines = [];
  for (const line of olines) {
    let text = line.trim();
    let color = 0;
    while ((m = text.match(/^\\xffc([\d:;<])/))) {
      color = m[1].charCodeAt(0) - 48;
      text = text.substring(6);
    }
    text = text.trim();
    if (!text.length) {
      break;
    }
    if (color === 3 || (color === 4 && text[0] === '+')) { // weird amulet of the viper issue
      statLines.push(text);
    }
  }

  const lastLine = statLines[statLines.length - 1];
  if (lastLine) {
    let remove = false;
    if (lastLine.includes(Locale.istrings[22745])) {
      result.ethereal = true;
      remove = true;
    }
    m = lastLine.match(new RegExp(Locale.strings.Socketable + " \\((\\d+)\\)"));
    if (m) {
      result.sockets = parseInt(m[1]);
      remove = true;
    }
    if (remove) {
      statLines.pop();
    }
  }

  if (result.unidentified) {
    delete result.name;
  }

  const socketStats = {};
  if (types.has('blun')) {
    socketStats.item_undeaddamage_percent = 50;
  }
  if (sockets) {
    for (const id of sockets) {
      gemStats(socketStats, id, types);
    }
  }
  delete socketStats.poison_count;

  const item = Data.items[result.base];
  const itype = item && Data.itemTypes[item.type];

  let itemStats = {};

  if (!result.unidentified) {
    if (result.quality === Quality.MAGIC || result.quality === Quality.RARE || result.quality === Quality.CRAFTED) {
      result.mods = {};
    }
    if (result.quality !== Quality.UNIQUE && result.quality !== Quality.SET) {
      if (Data.items[result.base].autoprefix) {
        result.auto = {};
      }
      if (itype?.staffmods)  {
        result.staff = {};
      }
    }
    if (result.quality === Quality.CRAFTED) {
      result.crafted = {};
    }
    if (result.quality === Quality.SUPERIOR || result.quality === Quality.RUNEWORD) {
      result.superior = {};
    }


    if (result.quality === Quality.UNIQUE || result.quality === Quality.SET) {
      if (!result.unique) return null;

      let uindex = 0;

      let stats = null, copyLines = null, statVals = null, item = null;
      do {
        result.unique = uniques[uindex++];
        item = Data.uniqueItems[result.unique] || Data.setItems[result.unique];
        const values = [];
        for (let i = 0; i < 12; ++i) {
          if (!item[`prop${i + 1}`]) continue;
          const min = item[`min${i + 1}`], max = item[`max${i + 1}`];
          values.push((min === max || max === 0) ? max : `{${i}}`);
        }
        copyLines = [...statLines];
        stats = uniqueStats({}, item, values, result);
        statVals = parseStats(copyLines, {...stats, ...socketStats});      
      } while (uindex < uniques.length && copyLines.length);

      if (copyLines.length) return null;

      itemStats = {...statVals};

      if (!subtractStats(statVals, socketStats, stats)) return null;

      const uniqueValues = parseUniqueStats(result, () => statVals);
      if (!uniqueValues) return null;
      result.uniqueValues = uniqueValues;
    } else if (result.quality === Quality.RUNEWORD) {
      const parsed = parseRunewordStats(result, socketStats, allStats => {
        itemStats = parseStats(statLines, allStats);
        return {...itemStats};
      });
      if (!parsed) return null;
      Object.assign(result, parsed);
    } else {
      let levelreq = 1;
      for (const line of lines) {
        const m = line.match(new RegExp(`^${Locale.strings.ItemStats1p} (\\d+)$`));
        if (m) levelreq = parseInt(m[1]);
      }

      const parsed = parseModStats(result, magic, socketStats, levelreq, statsSrc => {
        itemStats = parseStats(statLines, statsSrc);
        if (statLines.length) return null;
        return {...itemStats};
      });
      if (!parsed) return null;
      Object.assign(result, parsed);
    }
  }

  let totaldef = 0;
  for (const line of lines) {
    const m = line.match(new RegExp(`^${Locale.strings.ItemStats1h} (\\d+)$`));
    if (m) {
      totaldef = parseInt(m[1]);
    }
  }
  if (item.maxac) {
    // let's check set stats!
    if (result.quality === Quality.SET && !result.unidentified) {
      const equipped = [];
      let index = 0;
      const uniq = Data.setItems[result.unique];
      const uniqName = Locale.strings[uniq.index] || uniq.index;
      for (let i = olines.length - 1; i >= 0 && olines[i].length; --i) {
        const m = olines[i].match(/^\\xffc(\d)(.*)$/);
        if (!m) continue;
        const color = parseInt(m[1]);
        if (color !== Color.RED && color !== Color.GREEN) continue;
        if (m[2] === uniqName) index = equipped.length;
        equipped.push(color === Color.GREEN);
      }
      const partStats = itemsetPartStats(uniq, equipped, index, result);
      for (const entry of partStats) {
        for (const id in entry.stats) {
          itemStats[id] = (itemStats[id] || 0) + entry.stats[id];
        }
      }
    }

    let spawnedEd = result.unidentified || !!itemStats.item_armor_percent;
    if (result.quality === Quality.RUNEWORD) {
      const supStats = modsToStats({}, result.superior, result);
      spawnedEd = !!supStats.item_armor_percent;
    }

    let { minac, maxac } = item;
    let maxacEd = (spawnedEd ? maxac + 1 : maxac);

    let minsrc = totaldef, maxsrc = totaldef;
    if (itemStats.armorclass) {
      minsrc -= itemStats.armorclass;
      maxsrc -= itemStats.armorclass;
    }
    if (itemStats.item_armor_percent) {
      const factor = 1 + itemStats.item_armor_percent / 100;
      minsrc = Math.ceil(minsrc / factor);
      maxsrc = Math.ceil((maxsrc + 1) / factor) - 1;
    }
    if (result.ethereal) {
      minsrc = Math.ceil(minsrc / 1.5);
      maxsrc = Math.ceil((maxsrc + 1) / 1.5) - 1;
    }
    if (minsrc <= maxacEd && maxsrc >= minac) {
      result.defense = (Math.max(minsrc, minac) + Math.min(maxsrc, maxacEd)) >> 1;
    } else {
      const canEbug = result.quality === Quality.NORMAL || (result.quality === Quality.RUNEWORD && !Object.keys(result.superior).length);
      minsrc = Math.ceil(minsrc / 1.5);
      maxsrc = Math.ceil((maxsrc + 1) / 1.5) - 1;
      if (canEbug && minsrc <= maxac && maxsrc >= minac) {
        result.defense = (Math.max(minsrc, minac) + Math.min(maxsrc, maxac)) >> 1;
        result.ethbugged = true;
      } else {
        result.defense = maxacEd;
      }
    }
  }

  if (sockets && result.sockets) {
    let order = [...Array(result.sockets)].map((_, i) => i);
    if (result.sockets === 4 && item.invheight === 3) {
      order = [0, 3, 2, 1];
    } else if (result.sockets === 5) {
      order = [2, 0, 4, 3, 1];
    } else if (result.sockets === 6) {
      order = [0, 2, 4, 1, 3, 5];
    }
    result.socketedItems = order.map(i => sockets[i]).filter(Boolean);
  }

  return result;
}
